/**
 * Settings → Integrations → PostGrid — /admin/settings/postgrid
 * Connection status, TEST/LIVE mode, sender/return address, default
 * letter configuration. API keys are server-side only, never shown.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mailbox, ShieldAlert, CheckCircle2, XCircle, Plug } from "lucide-react";
import api from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";

const card = "rounded-2xl bg-white border border-slate-200 p-5";

function Field({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
      <input data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
    </label>
  );
}

export default function AdminPostGridSettings() {
  const [data, setData] = useState(null);
  const [sender, setSender] = useState({});
  const [cfg, setCfg] = useState({});
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/integrations/postgrid").then(({ data: d }) => {
      setData(d); setSender(d.sender); setCfg(d.letter_config);
    }).catch(() => toast.error("Failed to load PostGrid settings"));
  }, []);
  useEffect(load, [load]);

  const saveSender = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.patch("/admin/integrations/postgrid", { sender, letter_config: cfg });
      setData(d); toast.success("PostGrid settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const changeMode = async (mode) => {
    if (mode === "live" && !window.confirm(
      "Switch PostGrid to LIVE mode?\n\nEvery approved mailout will print and post REAL physical letters and incur real charges. Continue?")) return;
    setBusy(true);
    try {
      const { data: d } = await api.patch("/admin/integrations/postgrid", { mode });
      setData(d); toast.success(`PostGrid mode set to ${mode.toUpperCase()}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Mode change failed"); }
    finally { setBusy(false); }
  };

  const testConnection = async (mode) => {
    setBusy(true); setTestResult(null);
    try {
      const { data: r } = await api.post("/admin/integrations/postgrid/test-connection", { mode });
      setTestResult({ ok: true, mode, msg: `Connected (${r.total_letters} letters on account)` });
      load();
    } catch (e) {
      setTestResult({ ok: false, mode, msg: e.response?.data?.detail || "Connection failed" });
    } finally { setBusy(false); }
  };

  if (!data) return <PortalShell><div className="p-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div></PortalShell>;
  const live = data.mode === "live";

  return (
    <PortalShell>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="postgrid-settings-page">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">Settings · Integrations</div>
        <h1 className="font-display text-3xl font-semibold text-slate-900 mt-2 flex items-center gap-2"><Mailbox className="w-7 h-7 text-cyan-700" /> PostGrid Print &amp; Mail</h1>
        <p className="text-sm text-slate-500 mt-2">Prints, envelopes and posts Property Campaign letters automatically. API keys are stored server-side only and are never displayed.</p>

        <div className={`mt-5 rounded-xl px-4 py-3 flex items-center gap-3 border ${live ? "bg-rose-50 border-rose-300" : "bg-amber-50 border-amber-300"}`} data-testid="postgrid-mode-banner">
          <ShieldAlert className={`w-5 h-5 ${live ? "text-rose-600" : "text-amber-600"}`} />
          <div className="text-sm">
            <b className={live ? "text-rose-700" : "text-amber-800"}>
              {live ? "LIVE MODE — approved mailouts create real physical mail and real charges" : "TEST MODE — letters are submitted to PostGrid's sandbox and are never physically mailed"}
            </b>
            {data.mode_changed_by && <div className="text-xs text-slate-500 mt-0.5">Mode set by {data.mode_changed_by}</div>}
          </div>
          <div className="ml-auto flex gap-2">
            <Button data-testid="postgrid-set-test" size="sm" variant={live ? "outline" : "default"} className={!live ? "bg-amber-600 hover:bg-amber-700" : ""} disabled={busy || !live} onClick={() => changeMode("test")}>Test mode</Button>
            <Button data-testid="postgrid-set-live" size="sm" variant={live ? "default" : "outline"} className={live ? "bg-rose-600 hover:bg-rose-700" : ""} disabled={busy || live || !data.keys_configured.live} onClick={() => changeMode("live")}>Live mode</Button>
          </div>
        </div>

        <div className={`${card} mt-5`} data-testid="postgrid-connection-card">
          <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wider mb-3 flex items-center gap-1.5"><Plug className="w-4 h-4" /> Connection</h2>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2" data-testid="postgrid-test-key-status">
              {data.keys_configured.test ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-rose-500" />}
              Test key {data.keys_configured.test ? "configured" : "missing"}
              <Button size="sm" variant="outline" className="ml-auto" disabled={busy || !data.keys_configured.test} onClick={() => testConnection("test")} data-testid="postgrid-test-connection-test">Test connection</Button>
            </div>
            <div className="flex items-center gap-2" data-testid="postgrid-live-key-status">
              {data.keys_configured.live ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-rose-500" />}
              Live key {data.keys_configured.live ? "configured" : "missing"}
              <Button size="sm" variant="outline" className="ml-auto" disabled={busy || !data.keys_configured.live} onClick={() => testConnection("live")} data-testid="postgrid-test-connection-live">Test connection</Button>
            </div>
          </div>
          {testResult && (
            <div data-testid="postgrid-test-result" className={`mt-3 text-sm font-semibold ${testResult.ok ? "text-emerald-700" : "text-rose-600"}`}>
              {testResult.mode.toUpperCase()}: {testResult.msg}
            </div>
          )}
          <div className="mt-3 text-xs text-slate-500 space-y-0.5">
            <div data-testid="postgrid-last-success">Last successful API interaction: <b>{data.last_success_at ? new Date(data.last_success_at).toLocaleString("en-AU") : "—"}</b></div>
            {data.last_error && <div data-testid="postgrid-last-error" className="text-rose-600">Latest API error ({data.last_error_at ? new Date(data.last_error_at).toLocaleString("en-AU") : ""}): {data.last_error}</div>}
          </div>
        </div>

        <div className={`${card} mt-5`} data-testid="postgrid-sender-card">
          <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wider mb-3">Sender / Return Address</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Company" value={sender.company_name} onChange={(v) => setSender({ ...sender, company_name: v })} testId="postgrid-sender-company" />
            <Field label="Phone" value={sender.phone} onChange={(v) => setSender({ ...sender, phone: v })} testId="postgrid-sender-phone" />
            <Field label="First name" value={sender.first_name} onChange={(v) => setSender({ ...sender, first_name: v })} testId="postgrid-sender-first" />
            <Field label="Last name" value={sender.last_name} onChange={(v) => setSender({ ...sender, last_name: v })} testId="postgrid-sender-last" />
            <Field label="Address" value={sender.address_line1} onChange={(v) => setSender({ ...sender, address_line1: v })} testId="postgrid-sender-address" />
            <Field label="Suburb / City" value={sender.city} onChange={(v) => setSender({ ...sender, city: v })} testId="postgrid-sender-city" />
            <Field label="State" value={sender.province_or_state} onChange={(v) => setSender({ ...sender, province_or_state: v })} testId="postgrid-sender-state" />
            <Field label="Postcode" value={sender.postal_or_zip} onChange={(v) => setSender({ ...sender, postal_or_zip: v })} testId="postgrid-sender-postcode" />
          </div>

          <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wider mt-6 mb-3">Default Letter Configuration</h2>
          <div className="flex flex-wrap gap-5 text-sm items-center">
            <label className="flex items-center gap-2"><input type="checkbox" data-testid="postgrid-cfg-color" checked={!!cfg.color} onChange={(e) => setCfg({ ...cfg, color: e.target.checked })} /> Colour printing</label>
            <label className="flex items-center gap-2"><input type="checkbox" data-testid="postgrid-cfg-double" checked={!!cfg.double_sided} onChange={(e) => setCfg({ ...cfg, double_sided: e.target.checked })} /> Double-sided</label>
            <label className="flex items-center gap-2">Address placement
              <select data-testid="postgrid-cfg-placement" value={cfg.address_placement || "top_first_page"} onChange={(e) => setCfg({ ...cfg, address_placement: e.target.value })} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
                <option value="top_first_page">Print address on first page (compact letterhead layout)</option>
                <option value="insert_blank_page">Insert separate address page (full letterhead design)</option>
              </select>
            </label>
            <label className="flex items-center gap-2">Cost per letter (AUD)
              <input data-testid="postgrid-cfg-unit-cost" type="number" min="0" step="0.01" placeholder="e.g. 3.10"
                value={cfg.unit_cost_aud ?? ""} onChange={(e) => setCfg({ ...cfg, unit_cost_aud: e.target.value })}
                className="w-24 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
            </label>
          </div>
          <p className="mt-2 text-xs text-slate-500">Set the print + postage cost per letter from your PostGrid plan — mailouts will show the estimated batch cost before you approve.</p>
          <Button data-testid="postgrid-save-settings" disabled={busy} onClick={saveSender} className="mt-5 rounded-full bg-slate-900">{busy ? "Saving…" : "Save settings"}</Button>
        </div>
      </div>
    </PortalShell>
  );
}
